import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Mail, CheckCircle } from 'lucide-react';
import { authApi } from '../services/api';

export default function EmailSent() {
  const location = useLocation();
  const email = location.state?.email || '';
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const handleResend = async () => {
    if (!email) {
      setResendMessage('Email address not found. Please register again.');
      return;
    }

    setResending(true);
    setResendMessage('');

    try {
      await authApi.resendVerification(email);
      setResendMessage('Verification email resent! Please check your inbox.');
    } catch {
      setResendMessage('Failed to resend email. Please try again later.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-teal-50">
      {/* Header with site branding */}
      <nav className="bg-primary text-white shadow-lg">
        <div className="w-full pl-0 pr-3 py-0.5">
          <div className="flex items-center justify-between h-28">
            <Link to="/" className="flex items-center space-x-2">
              {/* Watercolor logo matching landing page */}
              <picture>
                <source srcSet="/logo.webp" type="image/webp" />
                <img src="/logo.png" alt="Local Artist Fairs" className="h-28 w-auto" />
              </picture>
            </Link>
            <div className="flex space-x-4">
              <Link to="/" className="hover:text-secondary-light">Back to Site</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Email Sent Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
                <Mail className="h-8 w-8 text-secondary" />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">Check Your Email</h2>

              <p className="text-gray-600 mb-4">
                We've sent a verification link to:
              </p>

              {email && (
                <p className="text-lg font-semibold text-primary-dark mb-6">
                  {email}
                </p>
              )}

              <div className="bg-moonbone border border-teal-200 rounded-md p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-secondary mt-0.5 flex-shrink-0" />
                  <div className="text-left text-sm text-ink">
                    <p className="font-semibold mb-1">Next Steps:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Check your email inbox</li>
                      <li>Click the verification link</li>
                      <li>Log in to your account</li>
                    </ol>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-500 mb-6">
                The verification link will expire in 24 hours.
              </p>

              {/* Resend Section */}
              <div className="border-t pt-6">
                <p className="text-sm text-gray-600 mb-3">Didn't receive the email?</p>

                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="w-full bg-white text-secondary border border-primary px-6 py-2 rounded-md hover:bg-secondary-light/50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resending ? 'Sending...' : 'Resend Verification Email'}
                </button>

                {resendMessage && (
                  <p className={`mt-3 text-sm ${resendMessage.includes('resent') ? 'text-secondary' : 'text-red-600'}`}>
                    {resendMessage}
                  </p>
                )}
              </div>

              {/* Login Link */}
              <div className="mt-6 pt-6 border-t">
                <p className="text-sm text-gray-600 mb-3">
                  Already verified your email?
                </p>
                <Link
                  to="/login"
                  className="text-secondary hover:text-primary-dark font-semibold"
                >
                  Go to Login →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
